import {
  Icon,
  getTranslatedText,
  BackgroundIcon,
  AccessControl,
  B1,
  useUserMSPCheckHook,
  WorklogSection,
  AppContext,
  useCheckFeatureGating,
  ALL_MODULES
} from '@nile/components';
import { TaskProgressHolder } from '@nile/projects/src/Components/Elements';
import { DeleteModal } from '@nile/projects/src/Components/Modals';
import {
  Card,
  Heading,
  Button,
  Avatar,
  Link,
  TextStyle
} from '@superops/ui-design-system';
import { Suitcase } from '@styled-icons/fa-solid';
import { Groups } from '@styled-icons/material-sharp';
import React, { useState, useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import envConfig from '@nile/home/src/Config/env';
import { MoreVert } from '@styled-icons/material';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SVGIcon } from '@superops/super-icons';
import TicketSchedulingCard from '@nile/mangifera/src/Containers/CardContainers/TicketSchedulingCard';
import InvoicedItemsList from '@nile/components/src/Containers/InvoicedItemsList';
import { LinkedItemsList } from '@nile/components/src/Containers/LinkedItemsList';

const IconWrapper = styled.div`
  opacity: 0.6;
  margin-right: 10px;
`;

const MemberContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MemberItem = styled.div`
  width: 100%;
  padding: 10px 0;
  justify-content: space-between;
  .member-name {
    margin: 4px;
    &.icon-holder {
      display: flex;
      align-items: center;
    }
    &.bold {
      font-weight: 600;
    }
  }
  .delete-icon {
    margin-top: 4px;
    margin-left: 8px;
    cursor: pointer;
  }

  .owner-tag {
    margin-left: 20px;
    background: rgba(210, 210, 210, 0.85);
    border-radius: 4px;
    padding: 2px 10px;
    font-weight: 500;
    font-size: 12px;
  }
  .mr32 {
    margin-right: 32px;
  }
`;

const ProjectInsightCard = ({ intl, project, handleUpdate }) => {
  const {
    members,
    completedTasks,
    totalTasks,
    overdueTasks,
    plannedTasks,
    inProgressTasks,
    blockedTasks
  } = project;
  const isMSP = useUserMSPCheckHook();
  const flags = useFlags();
  const [popoverActive, setPopoverActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState();
  const [active, setActive] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const projectContext = useContext(ProjectContext);
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};

  const handleChange = useCallback(() => setActive(!active), [active]);
  const togglePopoverActive = useCallback((ind) => {
    setActiveIndex(ind);
    setPopoverActive((val) => !val);
  }, []);

  const delModal = React.createRef();

  const handleDelete = () => {
    const memberObj = { ...currentMember };
    const { name, ...rest } = memberObj;
    handleUpdate('deleteMembers', [rest]);
    setCurrentMember(null);
  };

  const sortMembers = (array) => {
    const sortedArray = [];
    array.map((el) => {
      if (el.type !== 'OWNER') {
        sortedArray.push(el);
      } else {
        sortedArray.unshift(el);
      }
    });
    return sortedArray;
  };

  const sortedMembers = sortMembers(members);

  const isSchedulingAvailableInPlan = useCheckFeatureGating(
    'calendar_scheduling'
  );

  return (
    <div className="actr full-width p20 flex col g20">
      {isMSP && (
        <AccessControl
          reference={projectContext.features}
          roleToCheck={{
            roles: ['projectWorklog.add', 'serviceCatalog.view']
          }}
          render={
            <WorklogSection
              project={projectContext.project}
              module="PROJECT"
              workItem={{
                ...projectContext.project,
                workId: projectContext.project?.projectId,
                module: 'PROJECT'
              }}
              hideTaskWorkItem
            />
          }
        />
      )}
      {project?.workItem?.associations?.length > 0 && (
        <div className="full-width">
          <LinkedItemsList
            module={ALL_MODULES.ticket}
            roles={['tickets.view']}
            workItems={project?.workItem?.associations}
            headerLabelId="label.linked.ticket"
          />
        </div>
      )}
      {isMSP && (
        <AccessControl
          roleToCheck={{
            roles: ['invoice.view']
          }}
          render={
            <div className="w100">
              <Card customClassName="full-width">
                <InvoicedItemsList items={project.invoices} />
              </Card>
            </div>
          }
        />
      )}
      {isMSP && isSchedulingAvailableInPlan?.enabled && (
        <div className="w100">
          <Card customClassName="full-width" padding="sizeSmall">
            <TicketSchedulingCard
              workItem={{
                ...projectContext?.project,
                workId: projectContext?.project?.projectId,
                subject: projectContext?.project?.title
              }}
              dispatcherTechnicians={[]}
              module={ALL_MODULES.project}
            />
          </Card>
        </div>
      )}
      <div className="full-width">
        <Card>
          <div className="p20">
            <div className="fx actr">
              <IconWrapper>
                <SVGIcon name="ProjectLogo" color="#323232" />
              </IconWrapper>
              <Heading element="h3">
                <B1>
                  <FormattedMessage id="project.details.insights.details" />
                </B1>
              </Heading>
            </div>
            <div className="fx mt10 mb-20">
              <TaskProgressHolder
                completed={completedTasks}
                total={totalTasks}
                overdue={overdueTasks}
                open={plannedTasks}
                progress={inProgressTasks}
                blocked={blockedTasks}
              />
            </div>
            <div className="fx actr mt10">
              <IconWrapper>
                <SVGIcon name="TechnicianGroup" color="#323232" />
              </IconWrapper>
              <Heading element="h3">
                <B1>
                  <FormattedMessage id="project.details.insights.members" />
                </B1>
              </Heading>
            </div>
            <MemberContainer>
              {members.length > 0
                ? sortedMembers.map((member, index) => {
                    return (
                      <MemberItem
                        key={member.user.name}
                        className="visibilityContainer flex actr"
                      >
                        <div
                          className={`member-name icon-holder ${
                            member.type === 'OWNER' ? 'bold' : ''
                          }`}
                        >
                          <div className="mr-5">
                            <Avatar customer name="Owner" size="extraSmall" />
                          </div>
                          <div>{`${member.user.firstName} ${member.user.lastName}`}</div>
                          {member.type === 'OWNER' && (
                            <div className="owner-tag"> Owner</div>
                          )}
                        </div>
                        <div className="fx">
                          <div
                            className={`member-name ${
                              member.type === 'OWNER' &&
                              currentUser.userId === member.user.userId // Style fix if currentUser is not Owner
                                ? 'mr32'
                                : ''
                            }`}
                          >
                            {`${member.completedTasks}/${member.totalTasks}`}
                          </div>
                          {member.type !== 'OWNER' && (
                            <>
                              <AccessControl
                                reference={projectContext.features}
                                roleToCheck="projectManagementProject.edit"
                                render={
                                  <div
                                    className="options visibilityActions"
                                    data-qa="memberOptions"
                                  >
                                    <div
                                      className="delete-icon"
                                      onClick={() => {
                                        setCurrentMember(member.user);
                                        handleChange();
                                      }}
                                    >
                                      <SVGIcon name="Delete" />
                                    </div>
                                  </div>
                                }
                              />
                            </>
                          )}
                        </div>
                      </MemberItem>
                    );
                  })
                : getTranslatedText(intl, 'project.details.insights.noMember')}
            </MemberContainer>
            <DeleteModal
              description={getTranslatedText(
                intl,
                'project.details.delete.text'
              )}
              handleDeleteConfirmation={handleDelete}
              title={getTranslatedText(intl, 'project.details.delete.header')}
              reference={delModal}
              active={active}
              handleChange={handleChange}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default injectIntl(ProjectInsightCard);
